// Shared gifting logic for the admin pages. The Subscriptions tab has a
// free-form "gift by email" card; the Users tab gifts a specific row via a
// modal. Both talk to POST /api/admin/gift{,/revoke} through this one hook so
// the request/response handling stays in a single place.
import { useState } from 'preact/hooks';
import { api, ApiException } from '../../lib/api';
import { fmtDate } from './ui';

export const GIFT_DURATIONS = [
  { value: 'week', label: '1 week' },
  { value: 'month', label: '1 month' },
  { value: 'three_month', label: '3 months' },
  { value: 'six_month', label: '6 months' },
  { value: 'year', label: '1 year' },
  { value: 'lifetime', label: 'Lifetime' },
] as const;

export type GiftResponse = { ok: boolean; email: string; expiresAt?: string };

export type GiftMsg = { ok: boolean; text: string } | null;

// useGift centralizes the busy flag, result message, and the two calls. The
// grant lands in the Subscriptions table only once RevenueCat's webhook
// delivers the event, so the success copy says so.
export function useGift() {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<GiftMsg>(null);

  const run = async (
    path: string,
    body: Record<string, string>,
    okText: (r: GiftResponse) => string,
  ): Promise<boolean> => {
    if (busy) return false;
    setBusy(true);
    setMsg(null);
    try {
      const r = await api<GiftResponse>(path, { method: 'POST', body: JSON.stringify(body) });
      setMsg({ ok: true, text: okText(r) });
      return true;
    } catch (e) {
      setMsg({ ok: false, text: e instanceof ApiException ? e.message : 'Request failed' });
      return false;
    } finally {
      setBusy(false);
    }
  };

  const gift = (email: string, duration: string) =>
    run('/api/admin/gift', { email, duration }, (r) =>
      `Granted pro to ${r.email}${r.expiresAt ? ` until ${fmtDate(r.expiresAt)}` : ''}. ` +
      'It appears under Subscriptions once the RevenueCat webhook arrives.');

  const revoke = (email: string) =>
    run('/api/admin/gift/revoke', { email }, (r) =>
      `Revoked gifted pro from ${r.email}. Store purchases are unaffected.`);

  return { busy, msg, setMsg, gift, revoke };
}
